import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, MessageSquare, Layers, Tags, TrendingUp, ArrowRight, Database, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getAdminStats, getAdminInquiries, seedDatabase } from '../../services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSeeding, setIsSeeding] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsData, inquiriesData] = await Promise.all([
        getAdminStats(),
        getAdminInquiries()
      ]);
      setStats(statsData);
      setRecentInquiries(inquiriesData.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeedDatabase = async () => {
    if (!window.confirm('This will MERGE the master catalog with your existing data. Your manual changes (logos, edits) will be PRESERVED. Only missing categories, brands, and products will be added. Continue?')) {
      return;
    }
    
    setIsSeeding(true);
    try {
      const result = await seedDatabase();
      alert(`Success! ${result.message}`);
      fetchData(); // Refresh stats
    } catch (error) {
      console.error('Error syncing database:', error);
      alert('Error syncing database. Please try again.');
    } finally {
      setIsSeeding(false);
    }
  };

  const statCards = [
    { 
      label: 'Total Categories', 
      value: stats?.totalCategories || 0, 
      icon: Layers, 
      color: 'var(--primary-teal)',
      link: '/admin/products'
    },
    { 
      label: 'Total Brands', 
      value: stats?.totalBrands || 0, 
      icon: Tags, 
      color: 'var(--gold-accent)',
      link: '/admin/products'
    },
    { 
      label: 'Total Products', 
      value: stats?.totalProducts || 0, 
      icon: Package, 
      color: 'var(--primary-teal)',
      link: '/admin/products'
    },
    { 
      label: 'New Inquiries', 
      value: stats?.newInquiries || 0, 
      icon: MessageSquare, 
      color: stats?.newInquiries > 0 ? '#ef4444' : 'var(--gold-accent)',
      link: '/admin/inquiries'
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--primary-teal)] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--primary-teal)' }}>
            Dashboard
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-medium)' }}>
            Welcome to Triton Global Trade Admin Panel
          </p>
        </div>
        <Button 
          onClick={handleSeedDatabase}
          disabled={isSeeding}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="seed-database-btn"
        >
          {isSeeding ? (
            <>
              <RefreshCw size={16} className="mr-2 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <Database size={16} className="mr-2" />
              Merge Product Data
            </>
          )}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Link to={stat.link} key={idx}>
              <Card className="card-hover border-0 shadow-md h-full">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs sm:text-sm" style={{ color: 'var(--text-medium)' }}>
                        {stat.label}
                      </p>
                      <p className="text-2xl sm:text-3xl font-bold mt-1" style={{ color: stat.color }}>
                        {stat.value}
                      </p>
                    </div>
                    <div 
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${stat.color}20` }}
                    >
                      <Icon size={20} style={{ color: stat.color }} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Recent Inquiries */}
      <Card className="border-0 shadow-md">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle style={{ color: 'var(--primary-teal)' }}>
            Recent Inquiries
          </CardTitle>
          <Link to="/admin/inquiries">
            <Button variant="ghost" size="sm" className="gap-1">
              View All <ArrowRight size={16} />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentInquiries.length === 0 ? (
            <p className="text-center py-8" style={{ color: 'var(--text-medium)' }}>
              No inquiries yet
            </p>
          ) : (
            <div className="space-y-4">
              {recentInquiries.map((inquiry) => (
                <div 
                  key={inquiry.id} 
                  className="flex items-start justify-between p-4 rounded-lg bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate" style={{ color: 'var(--text-dark)' }}>
                        {inquiry.name}
                      </p>
                      <span 
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          inquiry.status === 'new' 
                            ? 'bg-red-100 text-red-700'
                            : inquiry.status === 'read'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {inquiry.status}
                      </span>
                    </div>
                    <p className="text-sm truncate" style={{ color: 'var(--text-medium)' }}>
                      {inquiry.subject}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-light)' }}>
                      {new Date(inquiry.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/admin/products">
          <Card className="card-hover border-0 shadow-md cursor-pointer" style={{ backgroundColor: 'var(--primary-teal)' }}>
            <CardContent className="p-6 text-white">
              <Package size={32} className="mb-3" />
              <h3 className="text-lg font-bold">Manage Products</h3>
              <p className="text-sm opacity-80 mt-1">Add, edit, or remove products and categories</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/inquiries">
          <Card className="card-hover border-0 shadow-md cursor-pointer" style={{ backgroundColor: 'var(--gold-accent)' }}>
            <CardContent className="p-6 text-white">
              <MessageSquare size={32} className="mb-3" />
              <h3 className="text-lg font-bold">View Inquiries</h3>
              <p className="text-sm opacity-80 mt-1">Respond to customer inquiries and messages</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
